import { CubeQuery } from '@/types/cube';
import { Filter, Query } from '@cubejs-client/core';
import { DEFAULT_COUNTRY_MEMBER, getCountryDimensionForCube } from './countryFilterMapping';
import { UnsanitizedQuery } from './sanitizeCubeQuery';

function extractCubeName(member: string): string {
  const dotIndex = member.indexOf('.');
  return dotIndex === -1 ? member : member.substring(0, dotIndex);
}

export function extractCubeNamesFromQuery(query: CubeQuery | UnsanitizedQuery): string[] {
  const cubeNames = new Set<string>();
  const typedQuery = query as Query;

  if (typedQuery.measures) {
    for (const measure of typedQuery.measures) {
      cubeNames.add(extractCubeName(measure));
    }
  }
  if (typedQuery.dimensions) {
    for (const dimension of typedQuery.dimensions) {
      cubeNames.add(extractCubeName(dimension));
    }
  }
  if (typedQuery.timeDimensions) {
    for (const timeDimension of typedQuery.timeDimensions) {
      if (timeDimension?.dimension) {
        cubeNames.add(extractCubeName(timeDimension.dimension));
      }
    }
  }

  return Array.from(cubeNames);
}

function replaceCountryFilterMember(filter: Filter, targetMember: string): Filter {
  if ('member' in filter && filter.member === DEFAULT_COUNTRY_MEMBER) {
    return { ...filter, member: targetMember };
  }
  if ('and' in filter && Array.isArray(filter.and)) {
    return { ...filter, and: filter.and.map((nested: Filter) => replaceCountryFilterMember(nested, targetMember)) };
  }
  if ('or' in filter && Array.isArray(filter.or)) {
    return { ...filter, or: filter.or.map((nested: Filter) => replaceCountryFilterMember(nested, targetMember)) };
  }
  return filter;
}

export function resolveCountryFilters(query: CubeQuery, filters: Filter[]): CubeQuery;
export function resolveCountryFilters(query: UnsanitizedQuery, filters: Filter[]): UnsanitizedQuery;
// Last overload serves callers holding an un-narrowed union (sanitizeCubeQuery)
export function resolveCountryFilters(
  query: CubeQuery | UnsanitizedQuery,
  filters: Filter[],
): CubeQuery | UnsanitizedQuery;
export function resolveCountryFilters(
  query: CubeQuery | UnsanitizedQuery,
  filters: Filter[],
): CubeQuery | UnsanitizedQuery {
  if (!filters.length) return query;

  const cubeNames = extractCubeNamesFromQuery(query);
  let targetMember: string | undefined;
  for (const cubeName of cubeNames) {
    if (cubeName === 'hcp') continue;
    const dimension = getCountryDimensionForCube(cubeName);
    if (dimension !== DEFAULT_COUNTRY_MEMBER) {
      targetMember = dimension;
      break;
    }
  }
  if (!targetMember) return query;
  return {
    ...query,
    filters: filters.map(existingFilter => replaceCountryFilterMember(existingFilter, targetMember)),
  };
}
